import { processUploads, uploadFiles } from '@/services/bots';
import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileText } from 'lucide-react';
import Table from './Table';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import LoadingIndicator from '@/components/LoadingIndicator';
import Pagination from '@/components/Pagination';
import { SearchInput } from '@/components/SearchInput';
import { useGetTrainedData, useRemoveTrainedData } from '@/hooks/useBot';
import { ITrainData } from '@/types';
import FileUpload from '@/views/bot/components/FileUpload';
import { debounce } from 'lodash';
import { useAnalytics } from '@/hooks/useAnalytics';
import { CHATBOT_TRAINING_UPDATED } from '@/lib/analytics-events';
import SectionCard from './shared/SectionCard';

interface ITrainBotProps {
  isBackButton?: boolean;
  token: string;
}

const TrainBot: FC<ITrainBotProps> = ({ isBackButton = true, token }) => {
  const { capture } = useAnalytics();
  const {
    data: trainedData,
    isLoading: trainedDataLoading,
    refetch: refetchTrained,
    queryParams,
    setQueryParams,
    isFetching,
  } = useGetTrainedData(token);

  const { mutate: removeTrainedData, isPending: isRemoving } = useRemoveTrainedData();

  const fileTypes = ['PDF', 'pdf', 'MD', 'md'];
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [trainedList, setTrainedList] = useState<ITrainData[]>([]);
  const [searchText, setSearchText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    initialize();
  }, [token, trainedData]);

  const initialize = () => {
    setTrainedList(trainedData?.data || []);
  };

  const handleSearchLink = useCallback(
    debounce((searchText: string) => {
      setQueryParams((prev) => ({
        ...prev,
        page: 1,
        searchText,
      }));
    }, 500),
    [],
  );

  const handlePageChange = (page: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page,
    }));
  };

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    handleSearchLink(e.target.value);
  };

  const removeFile = async (data: ITrainData) => {
    removeTrainedData(
      {
        file_id: data.file_id,
        chatToken: token,
      },
      {
        onSuccess: () => toast.success('File removed successfully.'),
        onError: () => toast.error('Failed to remove the file. Please try again.'),
      },
    );
  };

  const onUpload = async () => {
    setIsLoading(true);
    try {
      // #5012: uploadFiles/processUploads now throw with the real reason (which file, and
      // why) instead of collapsing every failure into a guess about duplicates.
      const data = await uploadFiles(files);
      const { uploaded, failed } = await processUploads(data, token);

      capture(CHATBOT_TRAINING_UPDATED, {
        file_count: uploaded.length,
        failed_count: failed.length,
      });

      // Refresh the table from the hook that owns it. The previous code also called
      // getTrainedData() and pushed its paginated envelope straight into setTrainedList,
      // where a plain array is expected — a throw there reported the whole upload as
      // failed even though the files were already registered.
      refetchTrained();
      setFiles([]);
      setModalOpen(false);

      if (failed.length > 0) {
        toast.warning(
          `${uploaded.length} file${uploaded.length === 1 ? '' : 's'} uploaded. `
          + `Not added: ${failed.map((f) => `${f.name} — ${f.reason}`).join('; ')}`,
        );
      } else {
        toast.success('File uploaded. Training has started — status will update shortly.');
      }
    } catch (error: unknown) {
      const message = error instanceof Error && error.message
        ? error.message
        : 'Upload failed. Please try again.';
      toast.error(message);
    }
    setIsLoading(false);
  };

  if (trainedDataLoading) return <LoadingIndicator />;

  return (
    <>
      <LoadingIndicator isLoading={isLoading || isRemoving} />
      <SearchInput
        className='max-w-[450px] mb-4'
        placeholder='Search files...'
        value={searchText}
        onChange={handleSearchInputChange}
        isLoading={isFetching && !!queryParams?.searchText}
      />
      <SectionCard
        icon={FileText}
        title="Documents"
        subtitle="Upload PDF or Markdown (.md) files to train your chatbot on your content."
        action={<Button size="sm" onClick={() => setModalOpen(true)}>Add file</Button>}
      >
        <Table rows={trainedList} removeFile={removeFile} isLoading={isFetching} />
        <Pagination
          className='my-4'
          onPageChange={handlePageChange}
          totalPage={trainedData?.pagination?.totalPages}
          page={queryParams?.page}
          limit={queryParams?.limit}
        />
      </SectionCard>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className='sm:max-w-[560px]'>
          <DialogHeader>
            <DialogTitle>Add file</DialogTitle>
          </DialogHeader>
          <FileUpload files={files} setFiles={setFiles} type={fileTypes} />
          <DialogFooter>
            <Button variant='outline' onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button
              onClick={onUpload}
              disabled={files.length === 0 || isLoading}
            >
              {isLoading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TrainBot;
